import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

/**
 * Compact two-line overlay of the focused player's cumulative return against the
 * benchmark, over the session. Same data contract as the v1 PnL-evolution tab
 * (`player_history[].portfolio_return` vs `metrics[].market_index_return`), but
 * height-filling and chrome-trimmed so it sits inside a studio card. The x-axis
 * is hidden (period/day is shown in the tooltip) to keep the small panel clean.
 */

type XY = { x: number | string; y: number };

type Props = {
  playerName: string;
  playerData: XY[];
  /** Benchmark series. Omit (undefined) when the session has no benchmark of any
   *  kind — the chart then plots the player line alone (no empty benchmark line
   *  or legend entry). When provided, rendering is unchanged. */
  benchmarkData?: XY[];
  valueFormatter?: (v: number) => string;
  height?: number | string;
  /** Real-time sessions plot a datetime x-axis; historical plot day numbers. */
  isDateTimeX?: boolean;
};

const PLAYER_COLOR = '#0f5fd6'; // studio primary blue (matches MiniChart default)
const BENCH_COLOR = '#94a3b5'; // muted grey — the benchmark reads as a baseline

const ReturnVsBenchmarkChart: React.FC<Props> = ({
  playerName,
  playerData,
  benchmarkData,
  valueFormatter,
  height = '100%',
  isDateTimeX = false,
}) => {
  const fmt = valueFormatter ?? ((v: number) => `${v}`);

  const series = useMemo(
    () =>
      benchmarkData
        ? [
            { name: playerName, data: playerData },
            { name: 'Benchmark', data: benchmarkData },
          ]
        : [{ name: playerName, data: playerData }],
    [playerName, playerData, benchmarkData],
  );

  const options = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'line',
        height,
        parentHeightOffset: 0,
        animations: { enabled: false },
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: [PLAYER_COLOR, BENCH_COLOR],
      stroke: { curve: 'smooth', width: [2.5, 2], dashArray: [0, 5] },
      markers: { size: 0 },
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '11px',
        offsetY: 2,
        itemMargin: { horizontal: 8, vertical: 0 },
      },
      xaxis: {
        type: isDateTimeX ? 'datetime' : 'numeric',
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          formatter: (v: number) => fmt(v),
          style: { fontSize: '11px' },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          // RT sessions carry a real timestamp per point — show date + time,
          // pinned to UTC (no local-timezone adaptation) so the stored
          // wall-clock reads verbatim (e.g. benchmark at 23:30). Historical
          // sessions are unchanged (day-number x-axis).
          formatter: (v: number) =>
            isDateTimeX
              ? new Date(v).toLocaleString(undefined, {
                  timeZone: 'UTC',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })
              : `Day ${v}`,
        },
        y: { formatter: (v: number) => fmt(v) },
      },
      grid: {
        padding: { left: 6, right: 10, top: 0, bottom: 0 },
        strokeDashArray: 3,
      },
    }),
    [height, isDateTimeX, fmt],
  );

  return (
    <Chart
      options={options}
      series={series as ApexOptions['series']}
      type="line"
      height={height}
      width="100%"
    />
  );
};

export default ReturnVsBenchmarkChart;
